import { marked, Renderer, type Tokens } from 'marked'
import type { Language } from '../i18n/types'

/**
 * Warstwa treści bloga.
 *
 * Artykuły to pliki Markdown w `src/content/blog/`. Nazwa pliku niesie dwie
 * informacje i jest jedynym źródłem prawdy o adresie:
 *
 *     <slug>.<jezyk>.md      np. ai-w-rezerwacjach.pl.md  →  /blog/ai-w-rezerwacjach
 *
 * Dzięki temu dodanie artykułu = dodanie pliku. Nic nie trzeba rejestrować
 * w kodzie — `import.meta.glob` wciąga pliki na etapie builda, więc tekst
 * ląduje w bundlu i nie ma żadnego zapytania sieciowego przy czytaniu.
 */

export interface BlogPost {
  slug: string
  language: Language
  title: string
  lead: string
  author: string
  category: string
  /** ISO (YYYY-MM-DD) — sortowanie i <time datetime> */
  date: string
  cover?: string
  /** Markdown zamieniony na HTML, gotowy do wstrzyknięcia */
  html: string
  /** Szacowany czas czytania w minutach, min. 1 */
  readingMinutes: number
}

/**
 * Surowe pliki .md wciągnięte na etapie builda (eager — treść idzie do bundla).
 * Wzorzec `*.*.md` wymaga segmentu języka w nazwie, więc pliki pomocnicze
 * (np. README.md z instrukcją redakcyjną) nie są brane za artykuły.
 */
const rawFiles = import.meta.glob('./blog/*.*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

/**
 * Minimalny parser frontmattera. Świadomie nie sięgamy po gray-matter —
 * ta biblioteka wymaga Buffera z Node'a i w przeglądarce trzeba ją polyfillować.
 * Nasz format to płaskie `klucz: wartość`, więc pełny parser YAML byłby
 * kilkanaście kilobajtów za rozpoznawanie składni, której i tak nie używamy.
 */
function parseFrontmatter(raw: string): {
  meta: Record<string, string>
  body: string
} {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/)
  if (!match) return { meta: {}, body: raw }

  const meta: Record<string, string> = {}
  for (const line of match[1].split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue

    const separator = trimmed.indexOf(':')
    if (separator === -1) continue

    const key = trimmed.slice(0, separator).trim()
    let value = trimmed.slice(separator + 1).trim()
    // Cudzysłowy są opcjonalne — potrzebne tylko gdy tytuł zawiera dwukropek.
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }
    if (key) meta[key] = value
  }

  return { meta, body: match[2] }
}

/** ~200 słów na minutę — przyjęty standard dla tekstu czytanego na ekranie. */
function estimateReadingMinutes(markdown: string): number {
  const words = markdown.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

marked.setOptions({ gfm: true, breaks: false })

/**
 * Tabela bywa szersza niż miara tekstu — na telefonie rozpychałaby całą stronę
 * w poziomie. Owijamy ją w kontener, który przewija się sam (styl `.table-wrapper`
 * czeka w `index.css`). Markdown nie ma składni na taki wrapper, więc jedynym
 * miejscem, gdzie da się go dołożyć, jest generowanie HTML-a.
 */
marked.use({
  renderer: {
    table(this: Renderer, token: Tokens.Table) {
      // `this` niesie referencję do parsera, więc domyślną implementację
      // wołamy przez prototyp, a nie na osobnej instancji renderera.
      return `<div class="table-wrapper">${Renderer.prototype.table.call(this, token)}</div>`
    },
  },
})

function buildPost(path: string, raw: string): BlogPost | null {
  // ./blog/ai-w-rezerwacjach.pl.md → ['ai-w-rezerwacjach', 'pl']
  const filename = path.split('/').pop()?.replace(/\.md$/, '') ?? ''
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) {
    console.warn(
      `[blog] Pomijam "${path}" — nazwa pliku musi mieć format <slug>.<pl|en>.md`
    )
    return null
  }

  const slug = filename.slice(0, lastDot)
  const language = filename.slice(lastDot + 1)
  if (language !== 'pl' && language !== 'en') {
    console.warn(`[blog] Pomijam "${path}" — nieznany język "${language}".`)
    return null
  }

  const { meta, body } = parseFrontmatter(raw)
  if (!meta.tytul || !meta.data) {
    console.warn(`[blog] Pomijam "${path}" — brak wymaganego pola: tytul lub data.`)
    return null
  }

  return {
    slug,
    language,
    title: meta.tytul,
    lead: meta.lead ?? '',
    author: meta.autor ?? 'Solid Labs',
    category: meta.kategoria ?? '',
    date: meta.data,
    cover: meta.okladka || undefined,
    html: marked.parse(body) as string,
    readingMinutes: estimateReadingMinutes(body),
  }
}

/** Wszystkie artykuły, najnowsze pierwsze. */
const allPosts: BlogPost[] = Object.entries(rawFiles)
  .map(([path, raw]) => buildPost(path, raw))
  .filter((post): post is BlogPost => post !== null)
  .sort((a, b) => b.date.localeCompare(a.date))

/**
 * Wybiera wersję artykułu dla danego języka. Gdy tłumaczenia brakuje, wraca
 * wersja polska (język bazowy), a w ostateczności jakakolwiek istniejąca.
 * Lepiej pokazać czytelnikowi tekst w drugim języku z adnotacją niż udawać,
 * że artykułu nie ma — dlatego lista jest zawsze kompletna, niezależnie od
 * stanu tłumaczeń.
 */
function pickVersion(versions: BlogPost[], language: Language): BlogPost {
  return (
    versions.find((v) => v.language === language) ??
    versions.find((v) => v.language === 'pl') ??
    versions[0]
  )
}

export function getPosts(language: Language): BlogPost[] {
  const bySlug = new Map<string, BlogPost[]>()
  for (const post of allPosts) {
    const versions = bySlug.get(post.slug)
    if (versions) versions.push(post)
    else bySlug.set(post.slug, [post])
  }

  return [...bySlug.values()]
    .map((versions) => pickVersion(versions, language))
    .sort((a, b) => b.date.localeCompare(a.date))
}

export function getPost(slug: string, language: Language): BlogPost | undefined {
  const versions = allPosts.filter((p) => p.slug === slug)
  return versions.length ? pickVersion(versions, language) : undefined
}

/** Kategorie występujące w danym języku — do filtrów na liście. */
export function getCategories(language: Language): string[] {
  const seen = new Set<string>()
  for (const post of getPosts(language)) {
    if (post.category) seen.add(post.category)
  }
  return [...seen]
}

/** Data w formacie długim, zgodnie z aktywnym językiem. */
export function formatDate(iso: string, language: Language): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso

  return new Intl.DateTimeFormat(language === 'pl' ? 'pl-PL' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed)
}
